import { Badge } from '~/components/ui/badge';
import type { OptimizationContentType } from '~/types/enrichment';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EnrichmentOptContentProps {
  /** Raw optimized content string from the enrichment API. */
  value: string;
  /** Rendering strategy — drives which parser and layout is used. */
  type: OptimizationContentType;
  /** `data-testid` root; derives `-tag-{tag}`, `-item`, `-attribute-{key}` (+ `-key`, `-value`), `-qa-item`, `-field-{key}`. */
  testId?: string;
}

/** Kebab-cases a tag / key so it can key a `data-testid`. */
function toTestIdSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders a single enrichment optimization value in the appropriate format
 * (tags, bullets, attributes, Q&A, structured JSON, or plain text) based on
 * the `type` prop. All parsing is done locally; no API calls.
 */
export function EnrichmentOptContent({
  value,
  type,
  testId = 'enrichment-opt-content',
}: EnrichmentOptContentProps) {
  if (type === 'tags') {
    const tags = value
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={testId}>
        {tags.map((tag) => (
          <Badge
            key={tag}
            data-testid={`${testId}-tag-${toTestIdSlug(tag)}`}
            variant="outline"
            className="border-blue-200 bg-blue-50 text-xs font-normal text-blue-700"
          >
            {tag}
          </Badge>
        ))}
      </div>
    );
  }

  if (type === 'bullets') {
    const items = value
      .split('|')
      .map((t) => t.trim())
      .filter(Boolean);
    return (
      <ul className="space-y-1.5 pl-1" data-testid={testId}>
        {items.map((item) => (
          <li
            key={item}
            className="flex items-start gap-2 text-sm leading-relaxed"
            data-testid={`${testId}-item`}
          >
            {/* Dot wrapper is one line-height tall so the dot centers on the first
                line and stays there when the item wraps. */}
            <span className="flex h-[1.625em] shrink-0 items-center" aria-hidden="true">
              <span className="h-1.5 w-1.5 rounded-full bg-neutral-400" />
            </span>
            {item}
          </li>
        ))}
      </ul>
    );
  }

  if (type === 'attributes') {
    // Pairs look like `Lily Attributes: Brand: Tommy Hilfiger`. Split each
    // pipe-delimited entry on the LAST colon so everything-before-the-last-colon
    // becomes the key and the rest is the value — survives multi-colon prefixes.
    const pairs = value
      .split('|')
      .map((raw) => raw.trim())
      .filter(Boolean)
      .map((raw) => {
        const idx = raw.lastIndexOf(':');
        if (idx === -1) return [raw, ''] as const;
        return [raw.slice(0, idx).trim(), raw.slice(idx + 1).trim()] as const;
      });
    return (
      <div className="divide-y" data-testid={testId}>
        {pairs.map(([key, val], i) => {
          const subValues = val
            .split('|')
            .map((s) => s.trim())
            .filter(Boolean);
          const pairTestId = `${testId}-attribute-${toTestIdSlug(key)}`;
          return (
            <div
              key={`${key}-${String(i)}`}
              className="flex items-start gap-2 py-1.5"
              data-testid={pairTestId}
            >
              <span
                className="text-muted-foreground w-40 shrink-0 text-xs"
                data-testid={`${pairTestId}-key`}
              >
                {key}
              </span>
              {subValues.length > 1 ? (
                <ul className="space-y-1" data-testid={`${pairTestId}-value`}>
                  {subValues.map((sv) => (
                    <li
                      key={sv}
                      className="flex items-start gap-1.5 text-xs leading-relaxed font-medium"
                    >
                      {/* One-line-tall wrapper keeps the dot on the first line when
                          the value wraps. */}
                      <span className="flex h-[1.625em] shrink-0 items-center" aria-hidden="true">
                        <span className="h-1.5 w-1.5 rounded-full bg-neutral-400" />
                      </span>
                      {sv}
                    </li>
                  ))}
                </ul>
              ) : (
                <span
                  className="text-xs leading-relaxed font-medium"
                  data-testid={`${pairTestId}-value`}
                >
                  {val}
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // 'qa' — parses a JSON array of {value} objects, concatenates all values,
  // splits by "|" then each segment by "A:" to extract Q and A pairs.
  if (type === 'qa') {
    let qaPairs: { q: string; a: string }[] = [];
    try {
      const parsed = JSON.parse(value) as { value: string }[];
      let combined = '';
      parsed.forEach((v) => {
        combined += v.value + ' ';
      });
      qaPairs = combined
        .split('|')
        .map((s) => s.split('A:'))
        .filter((s): s is [string, string, ...string[]] => s.length >= 2 && !!s[0]?.trim())
        .map(([q, a]) => ({ q: q.trim(), a: ('A: ' + a).trim() }));
    } catch {
      // fall through to plain text on parse failure
    }
    if (qaPairs.length > 0) {
      return (
        <div className="space-y-3" data-testid={testId}>
          {qaPairs.map(({ q, a }, i) => (
            <div
              key={i}
              className="space-y-0.5 text-sm leading-relaxed"
              data-testid={`${testId}-qa-item`}
            >
              <p className="text-muted-foreground" data-testid={`${testId}-qa-question`}>
                {q}
              </p>
              <p className="font-medium" data-testid={`${testId}-qa-answer`}>
                {a}
              </p>
            </div>
          ))}
        </div>
      );
    }
  }

  // 'structured-json' — parses a JSON object and renders each key-value pair
  // as a row with the key in bold and the value as plain text alongside it.
  if (type === 'structured-json') {
    let entries: [string, string][] = [];
    try {
      const parsed = JSON.parse(value) as Record<string, string>;
      entries = Object.entries(parsed).filter(([, v]) => v.trim().length > 0);
    } catch {
      // fall through to plain text on parse failure
    }
    if (entries.length > 0) {
      return (
        <div className="space-y-1.5" data-testid={testId}>
          {entries.map(([key, val]) => (
            <p
              key={key}
              className="text-sm leading-relaxed"
              data-testid={`${testId}-field-${toTestIdSlug(key)}`}
            >
              <span className="font-semibold">{key}:</span> {val}
            </p>
          ))}
        </div>
      );
    }
  }

  // 'plain' — always render as-is, never split on pipe characters.
  if (type === 'plain') {
    return (
      <p className="text-sm leading-relaxed" data-testid={testId}>
        {value}
      </p>
    );
  }

  // 'text' — if pipe-delimited, render as bullets; otherwise plain text.
  const textItems = value
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);
  if (textItems.length > 1) {
    return (
      <ul className="space-y-1.5 pl-1" data-testid={testId}>
        {textItems.map((item) => (
          <li
            key={item}
            className="flex items-start gap-2 text-sm leading-relaxed"
            data-testid={`${testId}-item`}
          >
            {/* Dot wrapper is one line-height tall so the dot centers on the first
                line and stays there when the item wraps. */}
            <span className="flex h-[1.625em] shrink-0 items-center" aria-hidden="true">
              <span className="h-1.5 w-1.5 rounded-full bg-neutral-400" />
            </span>
            {item}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p className="text-sm leading-relaxed" data-testid={testId}>
      {value}
    </p>
  );
}
